/* eslint-disable max-len */
/* eslint-disable react/no-danger, react/forbid-prop-types */
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader';
import classnames from 'classnames';
import { getLongFromShortCode } from '@homeaway/google-address-parser';
import Hero from '../../AEM/Hero';
import BrandLogos from '../../AEM/BrandLogos';
import ReadyToList from '../../AEM/ReadyToList';
import VrboFAQs from '../../VrboFAQs';
import LocationActions from '../../../actions/location';
import LeadActions from '../../../actions/lead';
import TranslationUtil from '../../../utils/TranslationUtil';
import VrboHeader from '../LypHeader';
import VrboFooter from '../../Footer';
import brandLogos from '../../AEM/BrandLogos/carousel_logos';
import ColumnPanel from '../../AEM/ColumnPanel';
import MediaText from '../../MediaText/MediaText';

// Shared safe default anchor for ULX hero
const SAFE_ULX_HERO_ANCHOR = '#ulx-hero';

// Very small URL sanitizer: allow http(s) and in-page anchors; otherwise fallback
const getSafeHref = (url) => {
    if (typeof url !== 'string' || url.trim() === '') return SAFE_ULX_HERO_ANCHOR;
    const trimmed = url.trim();
    if (trimmed.startsWith('#')) return trimmed; // in-page anchors
    const lowered = trimmed.toLowerCase();
    if (lowered.startsWith('http://') || lowered.startsWith('https://')) return trimmed;
    return SAFE_ULX_HERO_ANCHOR;
};

export function AemOkcTemplate({
    children,
    history,
    referrer,
    hero,
    content: sectionsContent,
    isFrboFlow,
    getTranslation,
    disclaimerText
}) {
    const renderSections = [];
    renderSections.push(
        <ReadyToList
            // eslint-disable-next-line react/prop-types
            {...sectionsContent.readyToList.data}
            isFrboFlow={false}
            loginPrompt={getTranslation('Vrbo.lyp.rtl.login')}
            key="ready-to-list"
        />
    );

    renderSections.push(
        <ColumnPanel
            {...sectionsContent.valueProps.data}
            columns={sectionsContent.valueProps.data?.columns || []}
            variant="V2"
            id="Learn-more"
            key="value-props"
        />
    );

    if (sectionsContent.testimonial && sectionsContent.testimonial.data) {
        const testimonialData = sectionsContent.testimonial.data;
        renderSections.push(
            <MediaText
                headline={testimonialData.title}
                copy={testimonialData.description}
                media={testimonialData.image}
                cta={testimonialData.cta}
                sectionId="Testimonials"
                disclaimerText={disclaimerText}
                ctaType={testimonialData.ctaType || 'link'}
                key="okc-testimonial"
            />
        );
    }

    // OKC: Three-step explainer section using ColumnPanel
    if (sectionsContent.okc && sectionsContent.okc.data) {
        const { data: okcData } = sectionsContent.okc;
        renderSections.push(
            <ColumnPanel
                {...okcData}
                columns={okcData.columns || []}
                variant="V2"
                id="OneKeyCash-steps"
                key="okc-steps"
            />
        );

        // Add CTA section if it exists
        if (okcData.cta?.text) {
            renderSections.push(
                <section className={classnames('okc-cta-section', 'section-padding')} key="okc-cta">
                    <div className="okc-cta-container">
                        <a
                            className={classnames('okc-cta-link', 'btn', 'btn-primary')}
                            href={getSafeHref(okcData.cta.url)}
                        >
                            {okcData.cta.text}
                        </a>
                    </div>
                </section>
            );
        }
    }

    renderSections.push(<VrboFAQs key="vrbo-faqs" />);

    renderSections.push(
        <BrandLogos
            {...sectionsContent.brandLogos.data}
            logos={sectionsContent.brandLogos.data?.logos || brandLogos}
            key="brand-logos"
        />
    );

    const classes = classnames(
        'ulx-antialias',
        'ulx-aem-content-container',
        'aem-okc',
        { 'ulx-aem-vr': isFrboFlow }
    );

    return (
        <div className={classes}>
            <VrboHeader
                history={history}
                referrer={referrer}
                isFrboFlow={isFrboFlow}
            />
            <Hero
                {...hero}
                isFrboFlow
                heroV2Props={{
                    copy: hero.copy,
                    titleList: hero.rotatingText && hero.rotatingText.length > 0 ? hero.rotatingText : [hero.headline],
                    titleSuffix: hero.titleSuffix || ' with Vrbo',
                    accessibilityButton: null
                }}
            >
                {children}
            </Hero>
            {renderSections}

            <VrboFooter showCompliance showOkcNotice />
        </div>
    );
}

/* istanbul ignore next */
export const mapStateToProps = (state) => {
    const { app, lead, location } = state;
    const {
        aemData, brand, locale
    } = app;
    const {
        email, phoneValidation, emailValidation, phoneNumber, phoneCountryCodeDigits
    } = lead;
    const { address, addressValidation } = location;
    const { hero, content } = aemData;
    // OKC template is always VRBO flow
    const isFrboFlow = true;
    const translations = new TranslationUtil(app.translations, app.fallbackTranslations);
    const getTranslation = (translation, params = undefined) => translations.getTranslation(translation, params);
    const countriesByCode = translations.mapCountryTranslations();
    const countryList = [
        'us',
        'ca',
        'mx',
        'bz',
        'cr'
    ].map((countryCode) => ({
        text: countriesByCode[countryCode],
        value: countryCode.toUpperCase()
    }))
    // The phone list includes plaes that don't actually exist, so filter to countries with
    // actual valid ISO country codes
        .filter((item) => !!getLongFromShortCode(item.value.toUpperCase()))
        .sort((a, b) => a.text.localeCompare(b.text));
    countryList.unshift({
        text: '--',
        value: ''
    });

    return {
        hero,
        content,
        isFrboFlow,
        email,
        address,
        countryList,
        addressValidation,
        getTranslation,
        brand,
        locale,
        phoneValidation,
        emailValidation,
        emailValidationErrorText: translations.getTranslation('validation.error.isValidEmail'),
        disclaimerText: translations.getTranslation('Vrbo.videoTestimonial.okc.disclaimer.text'),
        phoneNumber,
        phoneCountryCodeDigits
    };
};
const ValidationObject = {
    isValid: PropTypes.bool.isRequired,
    error: PropTypes.string
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch, ownProps) => ({
    setFirstName: (firstName) => dispatch(LeadActions.setFirstName(firstName)),
    setLastName: (lastName) => dispatch(LeadActions.setLastName(lastName)),
    setEmail: (email) => dispatch(LeadActions.setEmail(email)),
    setAddressField:
        (fieldName, value) => {
            dispatch(LocationActions.setAddressField(
                fieldName,
                value,
                ownProps.features.isLatinInputEnabled,
                null
            ));
        },
    validateEmail: (email) => dispatch(LeadActions.validateEmail(email, false, true))
});

AemOkcTemplate.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object
    ]).isRequired,
    history: PropTypes.shape({
        replace: PropTypes.func,
        location: PropTypes.shape({
            pathname: PropTypes.string
        }).isRequired
    }).isRequired,
    referrer: PropTypes.string.isRequired,
    hero: PropTypes.shape({
        image: PropTypes.shape({
            url: PropTypes.string,
            alt: PropTypes.string
        }),
        headline: PropTypes.string,
        copy: PropTypes.string,
        rotatingText: PropTypes.arrayOf(PropTypes.string),
        titleSuffix: PropTypes.string
    }).isRequired,
    content: PropTypes.shape({
        readyToList: PropTypes.shape({}),
        valueProps: PropTypes.shape({
            data: PropTypes.shape({
                headline: PropTypes.string,
                subheadline: PropTypes.string,
                columns: PropTypes.arrayOf(PropTypes.shape({
                    index: PropTypes.number,
                    head: PropTypes.string,
                    body: PropTypes.string,
                    image: PropTypes.shape({
                        url: PropTypes.string,
                        alt: PropTypes.string
                    })
                }))
            })
        }),
        vrImageText: PropTypes.shape({}),
        brandLogos: PropTypes.shape({
            data: PropTypes.shape({
                headline: PropTypes.string,
                closingCopy: PropTypes.string,
                logos: PropTypes.arrayOf(PropTypes.shape({
                    url: PropTypes.string,
                    alt: PropTypes.string
                }))
            })
        }),
        learnMore: PropTypes.shape({}),
        faq: PropTypes.shape({
            type: PropTypes.string,
            data: PropTypes.shape({
                headline: PropTypes.string,
                tabs: PropTypes.arrayOf(PropTypes.shape({
                    title: PropTypes.string,
                    questions: PropTypes.arrayOf(PropTypes.shape({
                        question: PropTypes.string,
                        answer: PropTypes.string
                    }))
                }))
            })
        }),
        okc: PropTypes.shape({
            type: PropTypes.string,
            data: PropTypes.shape({
                headline: PropTypes.string,
                subheadline: PropTypes.string,
                columns: PropTypes.arrayOf(PropTypes.shape({
                    index: PropTypes.number,
                    key: PropTypes.string,
                    head: PropTypes.string,
                    body: PropTypes.string,
                    image: PropTypes.shape({
                        url: PropTypes.string,
                        alt: PropTypes.string
                    })
                })),
                cta: PropTypes.shape({
                    url: PropTypes.string,
                    text: PropTypes.string
                })
            })
        }),
        propertyManager: PropTypes.shape({
            data: PropTypes.shape({
                headline: PropTypes.string,
                subcopy: PropTypes.string,
                cta: PropTypes.shape({
                    text: PropTypes.string,
                    url: PropTypes.string
                }),
                loginHeadline: PropTypes.string
            })
        }),
        stickyBanner: PropTypes.shape({
            data: PropTypes.shape({
                text: PropTypes.string
            })
        }),
        testimonial: PropTypes.shape({
            type: PropTypes.string,
            data: PropTypes.shape({
                title: PropTypes.string,
                description: PropTypes.string,
                ctaType: PropTypes.string,
                cta: PropTypes.shape({
                    text: PropTypes.string,
                    url: PropTypes.string
                }),
                image: PropTypes.shape({
                    url: PropTypes.string,
                    alt: PropTypes.string
                })
            })
        })
    }).isRequired,
    isFrboFlow: PropTypes.bool.isRequired,
    firstName: PropTypes.string.isRequired,
    firstNameValidation: PropTypes.shape(ValidationObject).isRequired,
    setFirstName: PropTypes.func.isRequired,
    lastName: PropTypes.string.isRequired,
    lastNameValidation: PropTypes.shape(ValidationObject).isRequired,
    setLastName: PropTypes.func.isRequired,
    email: PropTypes.string.isRequired,
    setEmail: PropTypes.func.isRequired,
    address: PropTypes.shape({
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        stateProvince: PropTypes.string,
        postalCode: PropTypes.string,
        country: PropTypes.string
    }).isRequired,
    countryList: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string
    })).isRequired,
    addressValidation: PropTypes.shape({
        address1: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        address2: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        city: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        stateProvince: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        postalCode: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        country: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        })
    }).isRequired,
    getTranslation: PropTypes.func.isRequired,
    brand: PropTypes.string.isRequired,
    locale: PropTypes.shape({
        canonical: PropTypes.string,
        display: PropTypes.string
    }).isRequired,
    setAddressField: PropTypes.func.isRequired,
    phoneValidation: PropTypes.shape(ValidationObject).isRequired,
    emailValidation: PropTypes.shape(ValidationObject).isRequired,
    validateEmail: PropTypes.func.isRequired,
    emailValidationErrorText: PropTypes.string.isRequired,
    disclaimerText: PropTypes.string.isRequired,
    phoneCountryCodeDigits: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string.isRequired
};

export default hot(module)(connect(mapStateToProps, mapDispatchToProps)(AemOkcTemplate));
